import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HardDrive, Loader2, Cloud, Film, Database, CheckCircle2, AlertCircle, AlertTriangle, Wrench, ListVideo } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtSize = (mb) => {
  const n = Number(mb || 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} TB`;
  if (n >= 1024) return `${(n / 1024).toFixed(2)} GB`;
  return `${n.toFixed(1)} MB`;
};

export default function Storage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const backfillPlaylists = async () => {
    setBackfilling(true);
    try {
      const res = await api.post("/admin/playlists/backfill");
      const { videos_added, accounts_updated } = res.data;
      toast.success(videos_added > 0
        ? `Added ${videos_added} video${videos_added === 1 ? "" : "s"} across ${accounts_updated} account${accounts_updated === 1 ? "" : "s"} into their "All Videos" playlist`
        : `All caught up — every video is already in its "All Videos" playlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const repairUrls = async () => {
    setRepairing(true);
    try {
      const res = await api.post("/admin/uploads/repair-urls");
      const { scanned, fixed } = res.data;
      toast.success(fixed > 0
        ? `Repaired ${fixed} of ${scanned} video${scanned === 1 ? "" : "s"} — playback URLs are now working`
        : `All good — scanned ${scanned} video${scanned === 1 ? "" : "s"}, nothing needed fixing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    api.get("/admin/storage")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load storage usage"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const orgs = data?.organizations || [];
  const warnPct = data?.warn_percent ?? 80;
  const overCount = orgs.filter((o) => o.warn).length;

  const stats = [
    { label: "Total storage used", value: fmtSize(data?.total_mb), icon: Database },
    { label: "Videos stored", value: data?.total_videos ?? 0, icon: Film },
    { label: "In Cloudflare R2", value: `${data?.r2_videos ?? 0} file${(data?.r2_videos ?? 0) === 1 ? "" : "s"}`, icon: Cloud },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">Storage</h1>
        <p className="text-gray-400 mt-1.5 text-sm">Video storage used by each church, pulled from your Cloudflare R2 bucket.</p>
      </div>

      {/* R2 connection status */}
      <div data-testid="r2-status" className={"mb-6 flex items-center gap-3 rounded-xl border px-4 py-3 " + (data?.r2_enabled ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-amber-500/30 bg-amber-500/[0.06]")}>
        {data?.r2_enabled ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <AlertCircle className="h-5 w-5 text-amber-400" />}
        <div className="text-sm">
          {data?.r2_enabled
            ? <span className="text-gray-200">Connected to Cloudflare R2 — bucket <span className="font-mono text-amber-300">{data?.bucket}</span></span>
            : <span className="text-gray-200">R2 not configured — videos are stored on local disk</span>}
        </div>
      </div>

      {/* One-time repair: fix legacy broken playback URLs on R2 uploads */}
      {data?.r2_enabled && (
        <div data-testid="repair-urls-panel" className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-gray-800 bg-[#0E131F] px-5 py-4">
          <div className="text-sm">
            <p className="font-medium text-white flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-400" /> Repair playback URLs</p>
            <p className="text-gray-400 mt-0.5">Fix any videos uploaded before the auto-URL update whose saved link no longer plays.</p>
          </div>
          <button
            onClick={repairUrls}
            disabled={repairing}
            data-testid="repair-urls-btn"
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
          >
            {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
            {repairing ? "Repairing…" : "Repair now"}
          </button>
        </div>
      )}

      {/* One-click: backfill every account's "All Videos" playlist with existing videos */}
      <div data-testid="backfill-playlists-panel" className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-gray-800 bg-[#0E131F] px-5 py-4">
        <div className="text-sm">
          <p className="font-medium text-white flex items-center gap-2"><ListVideo className="h-4 w-4 text-amber-400" /> Backfill playlists</p>
          <p className="text-gray-400 mt-0.5">Drop every existing recording and upload into each account's "All Videos" playlist, so linked church playlists are never empty.</p>
        </div>
        <button
          onClick={backfillPlaylists}
          disabled={backfilling}
          data-testid="backfill-playlists-btn"
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
        >
          {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListVideo className="h-4 w-4" />}
          {backfilling ? "Backfilling…" : "Backfill now"}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        {stats.map((s, i) => (
          <motion.div key={s.label} data-testid={`storage-stat-${i}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-card border border-gray-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide"><s.icon className="h-4 w-4 text-amber-400" /> {s.label}</div>
            <p className="mt-2 font-display text-2xl font-bold text-white">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Per-church breakdown */}
      <div className="bg-card border border-gray-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-amber-400" />
            <span className="text-white font-semibold text-sm">Storage by church</span>
          </div>
          {overCount > 0 && (
            <span data-testid="storage-warn-count" className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
              <AlertTriangle className="h-3.5 w-3.5" /> {overCount} near limit
            </span>
          )}
        </div>
        {orgs.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-12">No videos stored yet.</p>
        ) : (
          <div className="divide-y divide-gray-800/60">
            {orgs.map((o, i) => (
              <motion.div key={o.organization} data-testid={`storage-row-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.04, 0.3) }} className="px-5 py-4">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <span className="text-white font-medium text-sm flex items-center gap-2">
                    {o.organization}
                    {o.warn && <span data-testid={`storage-warn-${i}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"><AlertTriangle className="h-3 w-3" /> {o.percent}%</span>}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-gray-400 shrink-0">
                    <span className="inline-flex items-center gap-1"><Film className="h-3.5 w-3.5" /> {o.videos}</span>
                    <span className="font-mono text-gray-300"><span className={o.warn ? "text-amber-300 font-semibold" : "text-amber-300 font-semibold"}>{fmtSize(o.size_mb)}</span> / {o.quota_gb} GB</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
                  <div className={"h-full rounded-full transition-all " + (o.warn ? "bg-gradient-to-r from-amber-500 to-red-500" : "gold-gradient")} style={{ width: `${Math.max(2, o.percent)}%` }} />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <p className="mt-4 text-[11px] text-gray-500 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-400" /> Churches are flagged when they pass {warnPct}% of their plan's storage quota.</p>
    </div>
  );
}
